import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import ReactMarkdown from 'react-markdown';
import { Send, Bot, User, Loader2, Wrench, Code } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MetaChat = () => {
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId] = useState(() => `meta-session-${Date.now()}`);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    loadChatHistory();
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const loadChatHistory = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.get(`${API}/chat/${sessionId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setMessages(response.data);
    } catch (error) {
      console.error('Error loading chat history:', error);
    }
  };

  const sendMessage = async () => {
    if (!inputMessage.trim()) return;

    const userMessage = {
      role: 'user',
      content: inputMessage,
      timestamp: new Date().toISOString()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputMessage('');
    setLoading(true);

    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.post(
        `${API}/chat/meta`,
        {
          session_id: sessionId,
          content: inputMessage
        },
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );

      setMessages(prev => [...prev, response.data]);
      
      // Проверяем были ли выполнены действия
      if (response.data.metadata?.actions_executed?.length > 0) {
        toast.success(`Выполнено действий: ${response.data.metadata.actions_executed.length}`);
      }
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error('Ошибка отправки сообщения');
      
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: 'Извините, произошла ошибка. Попробуйте еще раз.',
        timestamp: new Date().toISOString()
      }]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  return (
    <div className="h-full flex flex-col" data-testid="meta-chat-page">
      {/* Header */}
      <div className="bg-gradient-to-r from-orange-900/50 to-red-900/50 backdrop-blur-xl border-b border-orange-500/30 p-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-white mb-2 flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-orange-600 to-red-600 flex items-center justify-center">
                <Wrench className="w-6 h-6 text-white" />
              </div>
              Мета-Чат: Управление Системой
            </h1>
            <p className="text-orange-200">
              🔧 Кран который собирает сам себя - редактируйте код этой системы через AI!
            </p>
          </div>
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-6 space-y-4" data-testid="meta-messages-container">
        {messages.length === 0 ? (
          <div className="h-full flex items-center justify-center">
            <div className="text-center max-w-2xl">
              <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-orange-600/20 to-red-600/20 flex items-center justify-center mx-auto mb-6">
                <Code className="w-10 h-10 text-orange-400" />
              </div>
              <h2 className="text-2xl font-bold text-white mb-3">
                Управляйте самой системой AI Agents!
              </h2>
              <p className="text-gray-400 mb-8">
                Этот чат позволяет модифицировать код самой системы агентов. Агенты работают с файлами в /app/backend и /app/frontend
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-left">
                <Card className="glass-panel border-orange-500/20 bg-orange-500/5 p-4 hover:bg-orange-500/10 transition-all cursor-pointer" onClick={() => setInputMessage('Добавь новую функцию для экспорта проектов')}>
                  <p className="text-sm text-white">🔨 Добавь новую функцию</p>
                </Card>
                <Card className="glass-panel border-orange-500/20 bg-orange-500/5 p-4 hover:bg-orange-500/10 transition-all cursor-pointer" onClick={() => setInputMessage('Исправь баг в ProjectManager')}>
                  <p className="text-sm text-white">🐛 Исправь баг</p>
                </Card>
                <Card className="glass-panel border-orange-500/20 bg-orange-500/5 p-4 hover:bg-orange-500/10 transition-all cursor-pointer" onClick={() => setInputMessage('Создай новый endpoint для статистики')}>
                  <p className="text-sm text-white">🚀 Создай endpoint</p>
                </Card>
                <Card className="glass-panel border-orange-500/20 bg-orange-500/5 p-4 hover:bg-orange-500/10 transition-all cursor-pointer" onClick={() => setInputMessage('Улучши дизайн дашборда')}>
                  <p className="text-sm text-white">🎨 Улучши UI</p>
                </Card>
              </div>
            </div>
          </div>
        ) : (
          messages.map((message, index) => (
            <div
              key={index}
              className={`flex gap-4 message-enter ${
                message.role === 'user' ? 'justify-end' : 'justify-start'
              }`}
              data-testid={`meta-message-${message.role}-${index}`}
            >
              {message.role === 'assistant' && (
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-orange-600 to-red-600 flex items-center justify-center flex-shrink-0">
                  <Bot className="w-5 h-5 text-white" />
                </div>
              )}
              
              <div
                className={`max-w-3xl rounded-2xl p-4 ${
                  message.role === 'user'
                    ? 'bg-gradient-to-r from-orange-600 to-red-600 text-white'
                    : 'glass-panel border-orange-500/20 bg-orange-500/5 text-gray-100'
                }`}
              >
                {message.role === 'assistant' && message.agent_type && (
                  <div className="text-xs text-orange-300 mb-2 flex items-center gap-2">
                    <span className="px-2 py-1 rounded-md bg-orange-500/20">
                      {message.agent_type === 'dev' ? '👨‍💻 DevAgent (Meta)' : 
                       `🤖 ${message.agent_type}`}
                    </span>
                  </div>
                )}
                <div className="prose prose-invert max-w-none">
                  <ReactMarkdown>{message.content}</ReactMarkdown>
                </div>
                <div className="text-xs text-gray-400 mt-2">
                  {new Date(message.timestamp).toLocaleTimeString('ru-RU')}
                </div>
              </div>
              
              {message.role === 'user' && (
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-600 to-cyan-600 flex items-center justify-center flex-shrink-0">
                  <User className="w-5 h-5 text-white" />
                </div>
              )}
            </div>
          ))
        )}
        
        {loading && (
          <div className="flex gap-4 justify-start">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-orange-600 to-red-600 flex items-center justify-center flex-shrink-0">
              <Loader2 className="w-5 h-5 text-white animate-spin" />
            </div>
            <div className="glass-panel border-orange-500/20 bg-orange-500/5 rounded-2xl p-4">
              <div className="flex gap-2">
                <div className="w-2 h-2 rounded-full bg-orange-400 animate-bounce"></div>
                <div className="w-2 h-2 rounded-full bg-orange-400 animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                <div className="w-2 h-2 rounded-full bg-orange-400 animate-bounce" style={{ animationDelay: '0.2s' }}></div>
              </div>
            </div>
          </div>
        )}
        
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div className="border-t border-orange-500/30 bg-gradient-to-r from-orange-900/30 to-red-900/30 backdrop-blur-xl p-6">
        <div className="flex gap-4 items-end">
          <div className="flex-1">
            <textarea
              value={inputMessage}
              onChange={(e) => setInputMessage(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Например: 'Добавь новую страницу для аналитики' или 'Исправь баг в DeployTools'..."
              className="w-full glass-panel border-orange-500/30 bg-orange-500/5 text-white placeholder-gray-400 rounded-2xl p-4 resize-none focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
              rows={3}
              disabled={loading}
              data-testid="meta-chat-input"
            />
          </div>
          <Button
            onClick={sendMessage}
            disabled={loading || !inputMessage.trim()}
            className="h-14 px-8 bg-gradient-to-r from-orange-600 to-red-600 hover:from-orange-700 hover:to-red-700 text-white rounded-2xl"
            data-testid="meta-send-button"
          >
            {loading ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <Send className="w-5 h-5" />
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default MetaChat;